import { Injectable } from '@angular/core';

export interface AuthTokens {
  tenantId?: string;
  branchId?: string;
  accessToken: string;
  expiresAtUtc?: string;
  refreshToken?: string;
}

@Injectable({ providedIn: 'root' })
export class AuthTokenService {
  private static readonly sessionStorageKey = 'inbordo.admin.auth.session';
  private tokens: AuthTokens | null = this.readPersistedTokens();

  getTokens(): AuthTokens | null {
    return this.tokens ? { ...this.tokens } : null;
  }

  setTokens(tokens: AuthTokens): void {
    this.tokens = { ...tokens };
    this.persistTokens(this.tokens);
  }

  clear(): void {
    this.tokens = null;
    this.clearPersistedTokens();
  }

  getAccessToken(): string | null {
    return this.getTokens()?.accessToken ?? null;
  }

  getTenantId(): string | null {
    return this.getTokens()?.tenantId ?? null;
  }

  getBranchId(): string | null {
    const tokens = this.getTokens();
    if (tokens?.branchId) {
      return tokens.branchId;
    }
    if (!tokens?.accessToken) {
      return null;
    }
    try {
      const payloadPart = tokens.accessToken.split('.')[1];
      const normalized = payloadPart.replace(/-/g, '+').replace(/_/g, '/');
      const padded = normalized.padEnd(Math.ceil(normalized.length / 4) * 4, '=');
      const payload = JSON.parse(atob(padded)) as { bid?: string };
      return payload.bid ?? null;
    } catch {
      return null;
    }
  }

  getRefreshToken(): string | null {
    return this.getTokens()?.refreshToken ?? null;
  }

  private readPersistedTokens(): AuthTokens | null {
    if (!this.canUseSessionStorage()) {
      return null;
    }

    try {
      const raw = window.sessionStorage.getItem(AuthTokenService.sessionStorageKey);
      if (!raw) {
        return null;
      }

      const parsed = JSON.parse(raw) as Partial<AuthTokens>;
      if (!parsed || typeof parsed.accessToken !== 'string') {
        return null;
      }

      return {
        tenantId: parsed.tenantId,
        branchId: parsed.branchId,
        accessToken: parsed.accessToken,
        expiresAtUtc: parsed.expiresAtUtc,
        refreshToken: parsed.refreshToken
      };
    } catch {
      return null;
    }
  }

  private persistTokens(tokens: AuthTokens): void {
    if (!this.canUseSessionStorage()) {
      return;
    }

    try {
      window.sessionStorage.setItem(AuthTokenService.sessionStorageKey, JSON.stringify(tokens));
    } catch {
      // Ignore storage failures in local dev fallback mode.
    }
  }

  private clearPersistedTokens(): void {
    if (!this.canUseSessionStorage()) {
      return;
    }

    try {
      window.sessionStorage.removeItem(AuthTokenService.sessionStorageKey);
    } catch {
      // Ignore storage failures in local dev fallback mode.
    }
  }

  private canUseSessionStorage(): boolean {
    return typeof window !== 'undefined' && window.location.hostname === 'localhost';
  }
}
